import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface LeadFormData {
  full_name: string;
  email: string;
  phone: string;
  primary_interest: string;
  current_situation: string;
  message: string;
}
interface LeadFormProps {
  onSubmit: (data: LeadFormData) => void;
}
export function LeadForm({
  onSubmit
}: LeadFormProps) {
  const [formData, setFormData] = useState<LeadFormData>({
    full_name: '',
    email: '',
    phone: '',
    primary_interest: '',
    current_situation: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof LeadFormData, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePhone = (phone: string): boolean => {
    // Australian phone format: 04xx xxx xxx or +614xxxxxxxx or variations
    const cleanPhone = phone.replace(/\s/g, '');
    const phoneRegex = /^(\+?614|04)\d{8}$/;
    return phoneRegex.test(cleanPhone);
  };
  const validateField = (name: keyof LeadFormData, value: string): string => {
    if (name === 'full_name' && !value.trim()) {
      return 'Full name is required';
    }
    if (name === 'email') {
      if (!value.trim()) return 'Email is required';
      if (!validateEmail(value)) return 'Please enter a valid email address';
    }
    if (name === 'phone') {
      if (!value.trim()) return 'Phone number is required';
      if (!validatePhone(value)) return 'Please enter a valid Australian phone number';
    }
    if (name === 'primary_interest' && !value) {
      return 'Please select your primary interest';
    }
    if (name === 'message' && !value.trim()) {
      return 'Please tell us how we can help you';
    }
    return '';
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof LeadFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleBlur = (name: keyof LeadFormData) => {
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate all fields
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};
    Object.keys(formData).forEach(key => {
      const fieldName = key as keyof LeadFormData;
      if (fieldName !== 'current_situation') {
        // current_situation is optional
        const error = validateField(fieldName, formData[fieldName]);
        if (error) newErrors[fieldName] = error;
      }
    });
    setErrors(newErrors);
    setTouched({
      full_name: true,
      email: true,
      phone: true,
      primary_interest: true,
      message: true
    });
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        const leadData = {
          ...formData,
          source_page: 'Request Consultation CTA',
          submission_date: new Date().toISOString().split('T')[0],
          submission_time: new Date().toLocaleTimeString('en-AU'),
          status: 'New Lead'
        };
        // TODO: Send to backend API
        console.log('Lead Data Submitted:', leadData);
        setIsSubmitting(false);
        onSubmit(formData);
      }, 800);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.3
  }} data-id="element-121">
      <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6" data-id="element-122">
        Complete your details, then choose a time that suits you.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-123">
        {/* Row 1: Full Name + Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-124">
          <div data-id="element-125">
            <label htmlFor="full_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-126">
              Full Name *
            </label>
            <input type="text" id="full_name" name="full_name" value={formData.full_name} onChange={handleChange} onBlur={() => handleBlur('full_name')} className={`w-full bg-slate-50 dark:bg-[#0f1629] border rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${touched.full_name && errors.full_name ? 'border-red-500' : 'border-slate-200 dark:border-white/10'}`} placeholder="John Smith" data-id="element-127" />
            {touched.full_name && errors.full_name && <p className="text-red-400 text-xs mt-1" data-id="element-128">{errors.full_name}</p>}
          </div>

          <div data-id="element-129">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-130">
              Email Address *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={() => handleBlur('email')} className={`w-full bg-slate-50 dark:bg-[#0f1629] border rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${touched.email && errors.email ? 'border-red-500' : 'border-slate-200 dark:border-white/10'}`} placeholder="john@example.com" data-id="element-131" />
            {touched.email && errors.email && <p className="text-red-400 text-xs mt-1" data-id="element-132">{errors.email}</p>}
          </div>
        </div>

        {/* Row 2: Phone + Client Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-133">
          <div data-id="element-134">
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-135">
              Phone Number *
            </label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onBlur={() => handleBlur('phone')} className={`w-full bg-slate-50 dark:bg-[#0f1629] border rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${touched.phone && errors.phone ? 'border-red-500' : 'border-slate-200 dark:border-white/10'}`} placeholder="0400 000 000" data-id="element-136" />
            {touched.phone && errors.phone && <p className="text-red-400 text-xs mt-1" data-id="element-137">{errors.phone}</p>}
          </div>

          <div data-id="element-138">
            <label htmlFor="primary_interest" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-139">
              Client Type / Primary Interest *
            </label>
            <select id="primary_interest" name="primary_interest" value={formData.primary_interest} onChange={handleChange} onBlur={() => handleBlur('primary_interest')} className={`w-full bg-slate-50 dark:bg-[#0f1629] border rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none ${touched.primary_interest && errors.primary_interest ? 'border-red-500' : 'border-slate-200 dark:border-white/10'}`} data-id="element-140">
              <option value="" data-id="element-141">Select your primary interest...</option>
              <option value="First Home Buyer" data-id="element-142">First Home Buyer</option>
              <option value="Home Buyer" data-id="element-143">Home Buyer</option>
              <option value="Property Investor" data-id="element-144">Property Investor</option>
              <option value="Refinance" data-id="element-145">Refinance</option>
              <option value="Strategy Session" data-id="element-146">Strategy Session</option>
            </select>
            {touched.primary_interest && errors.primary_interest && <p className="text-red-400 text-xs mt-1" data-id="element-147">
                {errors.primary_interest}
              </p>}
          </div>
        </div>

        {/* Row 3: Current Situation */}
        <div data-id="element-148">
          <label htmlFor="current_situation" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-149">
            Current Situation (Optional)
          </label>
          <select id="current_situation" name="current_situation" value={formData.current_situation} onChange={handleChange} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none" data-id="element-150">
            <option value="" data-id="element-151">Select your current situation...</option>
            <option value="Just exploring options" data-id="element-152">
              Just exploring options
            </option>
            <option value="Ready in next 3 months" data-id="element-153">
              Ready in next 3 months
            </option>
            <option value="Actively looking now" data-id="element-154">Actively looking now</option>
            <option value="Need urgent help" data-id="element-155">Need urgent help</option>
          </select>
        </div>

        {/* Row 4: Message */}
        <div data-id="element-156">
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-157">
            What would you like help with? *
          </label>
          <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} onBlur={() => handleBlur('message')} className={`w-full bg-slate-50 dark:bg-[#0f1629] border rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none ${touched.message && errors.message ? 'border-red-500' : 'border-slate-200 dark:border-white/10'}`} placeholder="Tell us briefly about your current situation and goals..." data-id="element-158" />
          {touched.message && errors.message && <p className="text-red-400 text-xs mt-1" data-id="element-159">{errors.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-160">
          {isSubmitting ? 'Processing...' : <>
              Continue to Book Your Consultation
              <ArrowRightIcon className="w-5 h-5" data-id="element-161" />
            </>}
        </button>
      </form>
    </motion.div>;
}